import React, { useEffect, useRef } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";

const Table = ({
  columns,
  data,
  totalItems,
  handlePagination,
  pageNumber,
  keyField,
  pageSize,
  onTableChange,
  defaultSortDirection,
  defaultSort,
  expandRow,
  wrapperClassName = "",
  tableContainerClassName = "",
  paginationClassName = "",
  tableClassName = "",
  /** When true, renders a plain table with no paginator (all rows visible). */
  disablePagination = false,
  rowEvents,
}) => {
  const tableBlockRef = useRef(null);

  useEffect(() => {
    const scrollContainer = tableBlockRef.current;
    const pagination = scrollContainer?.querySelector(".pagnation-block");
    if (!scrollContainer || !pagination) return;

    let animationFrameId;
    const syncPaginationPosition = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(() => {
        pagination.style.transform = "";

        if (window.getComputedStyle(pagination).cssFloat !== "right") return;

        const containerRight = scrollContainer.getBoundingClientRect().right;
        const paginationRight = pagination.getBoundingClientRect().right;
        const offset = containerRight - paginationRight - 20;

        pagination.style.transform = `translateX(${offset}px)`;
      });
    };

    syncPaginationPosition();
    scrollContainer.addEventListener("scroll", syncPaginationPosition, {
      passive: true,
    });
    window.addEventListener("resize", syncPaginationPosition);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      scrollContainer.removeEventListener("scroll", syncPaginationPosition);
      window.removeEventListener("resize", syncPaginationPosition);
      pagination.style.transform = "";
    };
  }, [data?.length, pageSize, totalItems]);

  if (disablePagination) {
    return (
      <div className={`data-table-block ${wrapperClassName}`}>
        <div className={`data-table-container ${tableContainerClassName}`}>
          <BootstrapTable
            bordered={false}
            classes={`custom-table ${tableClassName}`}
            columns={columns}
            data={data}
            defaultSortDirection={defaultSortDirection || "asc"}
            defaultSort={defaultSort}
            keyField={keyField}
            onTableChange={onTableChange || (() => {})}
            pagination={false}
            remote={false}
            wrapperClasses="table-responsive"
            expandRow={expandRow}
            rowEvents={rowEvents}
          />
        </div>
      </div>
    );
  }

  let defaultPageSize = 10;
  if (!pageSize) pageSize = defaultPageSize;

  const options = {
    custom: true,
    sizePerPage: pageSize,
    totalSize: totalItems,
    page: pageNumber,
    onPageChange: (page) => {
      handlePagination(page);
    },
  };

  return (
    <div
      ref={tableBlockRef}
      className={`data-table-block ${wrapperClassName}`}
    >
      <PaginationProvider pagination={paginationFactory(options)}>
        {({ paginationProps, paginationTableProps }) => (
          <>
            <div className={`data-table-container ${tableContainerClassName}`}>
              <BootstrapTable
                bordered={false}
                classes={`custom-table ${tableClassName}`}
                columns={columns}
                data={data}
                defaultSortDirection={defaultSortDirection || "asc"}
                defaultSort={defaultSort}
                keyField={keyField}
                onTableChange={onTableChange || (() => {})}
                pagination={true}
                remote={true}
                wrapperClasses="table-responsive"
                {...paginationTableProps}
                expandRow={expandRow}
                rowEvents={rowEvents}
              />
            </div>
            <div className={`pagnation-block ${paginationClassName}`}>
              {totalItems > pageSize && (
                <PaginationListStandalone {...paginationProps} />
              )}
            </div>
          </>
        )}
      </PaginationProvider>
    </div>
  );
};

export default Table;
